import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { LearnMoreService } from './learn-more-dialog/learn-more.service';
import { isLearnMoreTopic, LearnMoreTopic } from './learn-more-dialog/learn-more.content';
import { CallerService } from '../services/caller.service';
import { WordTickerItem } from '../word-ticker/word-ticker.component';

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
})
export class HomePageComponent implements OnInit, AfterViewInit, OnDestroy {
  /** How often one headline word changes. With four lines, each line changes every 4s. */
  static readonly WORD_TICK_MS = 1000;

  constructor(
    private learnMore: LearnMoreService,
    private callerService: CallerService,
    private router: Router,
    private route: ActivatedRoute,
    private zone: NgZone,
  ) {}

  public buyWords: Array<string> = [
    'mattress',
    'carpet',
    'fridge',
    'table',
    'chairs',
    'gas cylinder',
  ]

  public sellWords: Array<string> = [
    'desk',
    'fan',
    'cooker',
    'bed',
    'chair',
    'kettle',
  ]

  /**
   * Line 3's cities, and the search each one opens. They're the cities corps members search for
   * ("corpers lodge in lagos / port harcourt / ibadan..." in Search Console). `state` must be the
   * search page's upper-case state name.
   */
  public lodgeCities: Array<{ name: string; label?: string; state: string }> = [
    { name: 'Lagos', state: 'LAGOS' },
    { name: 'Abuja', state: 'FCT - ABUJA' },
    // "Port Harcourt." doesn't fit the line on desktop, and corps members say PH anyway.
    { name: 'Port Harcourt', label: 'PH', state: 'RIVERS' },
    { name: 'Ibadan', state: 'OYO' },
    { name: 'Enugu', state: 'ENUGU' },
    { name: 'Owerri', state: 'IMO' },
    { name: 'Asaba', state: 'DELTA' },
    { name: 'Benin', state: 'EDO' },
    { name: 'Jos', state: 'PLATEAU' },
    { name: 'Kaduna', state: 'KADUNA' },
    { name: 'Kano', state: 'KANO' },
    { name: 'Katsina', state: 'KATSINA' },
  ]

  /** Line 4's verb. Similar lengths, since "your PPA." moves along with the word before it. */
  public ppaVerbs: Array<string> = ['Locate', 'Review']

  /** What each headline ticker shows (see <app-word-ticker>). */
  readonly buyItems: WordTickerItem[] = this.buyWords.map((word) => ({ text: `${word}.` }));
  readonly sellItems: WordTickerItem[] = this.sellWords.map((word) => ({ text: `${word}.` }));
  readonly cityItems: WordTickerItem[] = this.lodgeCities.map((city) => ({
    text: `${city.label ?? city.name}.`,
    link: { path: '/search', queryParams: { tab: 'accommodation', state: city.state }, title: `Lodges in ${city.name}` },
  }));
  readonly ppaVerbItems: WordTickerItem[] = this.ppaVerbs.map((verb) => ({ text: verb }));

  @ViewChild('headline') private headline?: ElementRef<HTMLElement>;
  /** False while the headline is scrolled out of view - the words wait (see startWordTickers). */
  private headlineOnScreen = true;

  buyWordIndex = 0;
  sellWordIndex = 0;
  cityIndex = 0;
  ppaVerbIndex = 0;

  private destroy$ = new Subject<void>();

  public showDisclaimer = sessionStorage.getItem('c_ng_disclaimer') ? Boolean(JSON.parse(sessionStorage.getItem('c_ng_disclaimer') || 'true')) : true;

  openLearnMore(topic: LearnMoreTopic): void {
    this.learnMore.open(topic);
  }

  ngOnInit(): void {
    // Already logged in - skip the marketing page and go straight into the
    // app instead of asking them to log in again.
    if (this.callerService.hasActiveSession()) {
      this.router.navigate(['/messages']);
      return;
    }

    this.startWordTickers();

    // A link to /?learn=accommodation (an ad, a share) opens that "Learn more"
    const topic = this.route.snapshot.queryParamMap.get(LearnMoreService.QUERY_PARAM);
    if (isLearnMoreTopic(topic)) {
      this.learnMore.open(topic);
    }
  }

  /**
   * One word changes per tick, working down the headline - buy, sell, city, verb, then buy again -
   * so no two lines move at once. Nothing moves for people who've asked for reduced motion, and the
   * words wait while nobody can see them (tab in the background, headline scrolled away), picking up
   * at the next line when they're back. The timer runs outside Angular, so a tick that changes
   * nothing doesn't re-render the page.
   */
  private startWordTickers() {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const advance = [
      () => (this.buyWordIndex = (this.buyWordIndex + 1) % this.buyWords.length),
      () => (this.sellWordIndex = (this.sellWordIndex + 1) % this.sellWords.length),
      () => (this.cityIndex = (this.cityIndex + 1) % this.lodgeCities.length),
      () => (this.ppaVerbIndex = (this.ppaVerbIndex + 1) % this.ppaVerbs.length),
    ];
    let nextLine = 0;
    this.zone.runOutsideAngular(() => {
      interval(HomePageComponent.WORD_TICK_MS)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (document.hidden || !this.headlineOnScreen) {
            return;
          }
          this.zone.run(() => advance[nextLine++ % advance.length]());
        });
    });
  }

  closeDisclaimer() {
    sessionStorage.setItem('c_ng_disclaimer', 'false');
    this.showDisclaimer = false;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    const headline = this.headline?.nativeElement;
    if (!headline || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver(([entry]) => (this.headlineOnScreen = entry.isIntersecting));
    observer.observe(headline);
    this.destroy$.subscribe(() => observer.disconnect());
  }

  get fullYear(): number {
    return new Date()?.getFullYear();
  }

  // Team members for home page (show subset)
  teamMembers: Array<{
    name: string;
    role: string;
    bio: string;
    imageUrl: string;
    linkedinUrl?: string;
  }> = [
    {
      name: 'Juliet Mogaba',
      role: 'Co-Founder & Product Lead',
      bio: 'Leads vision, product strategy, and execution. Served as Geography teacher in Abia State, 2018.',
      imageUrl: '/assets/images/Juliet_HS.png',
      linkedinUrl: 'https://www.linkedin.com/in/juliet-mogaba',
    },
    {
      name: 'Nwachukwu Ossai',
      role: 'Co-Founder & Tech Lead',
      bio: 'He loves building products that help people. Served in Umuahia, 2018.',
      imageUrl: '/assets/images/Chuks_HS.jpeg',
      linkedinUrl: 'https://linkedin.com/in/nwachukwu-ossai',
    },
    {
      name: 'Wisdom Thursday',
      role: 'Brand Strategist & Marketing Lead',
      bio: 'You know about corpers.ng, because of him. Drives positioning, messaging and growth.',
      imageUrl: '/assets/images/Wisdom_HS.jpg',
      linkedinUrl: 'https://www.linkedin.com/in/wisdombthursday',
    },
  ];

}
